import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  PLATFORM_ID,
  Inject,
  HostListener,
  AfterViewInit,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatService } from '../../core/services/chatllm.service';
import { LinkifyPipe } from '../../core/pipes/linkifyPipe';
import { format } from 'date-fns';
import { RouterModule } from '@angular/router';
import { ThemeService } from '../../core/services/theme.service';
import { IntlTelInputDirective } from '../../core/directives/intl-tel-input.directive';
import { BLOCKED_EMAIL_DOMAINS as BLOCKED_EMAIL_DOMAINS_LIST } from '../../core/utils/constants';

interface ChatWsMessage {
  type: 'token' | 'done' | 'error';
  data?: string;
  message?: string;
}

@Component({
  selector: 'app-chatllm',
  standalone: true,
  imports: [CommonModule, FormsModule, LinkifyPipe, RouterModule, IntlTelInputDirective],
  templateUrl: './chatllm.html',
  styleUrl: './chatllm.scss',
})
export class ChatLlmComponent implements OnInit, AfterViewInit, OnDestroy {
  isDarkMode = false;
  isInputOpen = false;
  showWindow = false;
  isConnected = false;
  input = '';
  displayPlaceholder = '';
  isScrollingDown = false;
  private lastScrollTop = 0;
  showButton = false;
  private streamingIndex: number | null = null;
  btnColor!: Record<string, string>;

  messages: {
    role: string;
    text: string;
    time: string;
    typing?: boolean;
    contactForm?: boolean;
    displayText?: string;
    formFields?: {
      label: string;
      key: string;
      type: string;
      value: string;
      required: boolean;
      error?: string;
      countryCode?: string;
    }[];
    formSubmitted?: boolean;
  }[] = [];

  private readonly BLOCKED_EMAIL_DOMAINS: string[] = BLOCKED_EMAIL_DOMAINS_LIST;

  placeholders: string[] = [
    'Ask about Infraon products...',
    'How does Infraon help with IT asset management?',
    'How can I reduce infrastructure costs?',
    'Explain incident response automation',
    'Show IT asset savings insights',
    'How can I optimize IT operations?',
  ];

  isConnecting = false;
  isChatOpen = true;

  private placeholderIndex = 0;
  private charIndex = 0;
  private deleting = false;
  private timeout?: ReturnType<typeof setTimeout>;

  private subscribed = false;
  private isBrowser: boolean;
  @ViewChild('chatWindow') chatWindow?: ElementRef<HTMLElement>;
  private resizeFrame: number | null = null;

  showWelcome = false;
  showChatButton = false;
  private welcomeTimer?: ReturnType<typeof setTimeout>;
  private welcomeAutoHideTimer?: ReturnType<typeof setTimeout>;

  @ViewChild('chatBody') chatBody!: ElementRef;
  @ViewChild('chatInput') chatInput!: ElementRef<HTMLInputElement>;

  constructor(
    public chat: ChatService,
    private cd: ChangeDetectorRef,
    @Inject(PLATFORM_ID) platformId: object,
    private themeService: ThemeService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.welcomeTimer = setTimeout(() => {
        this.showWelcome = true;
        this.cd.detectChanges();

        this.welcomeAutoHideTimer = setTimeout(() => {
          this.dismissWelcome();
          this.cd.detectChanges();
        }, 2000);
      }, 1500);
    }

    if (!this.timeout) {
      this.typeEffect();
    }
    this.isDarkMode = this.themeService.isDarkMode();
  }

  ngAfterViewInit() {}

  dismissWelcome() {
    clearTimeout(this.welcomeAutoHideTimer);
    this.showWelcome = false;
    this.showChatButton = true;
  }

  async initializeConnection() {
    if (this.isConnected || this.isConnecting) return;

    this.isConnecting = true;

    try {
      await this.chat.connectSecure();

      if (!this.subscribed) {
        this.chat.onMessage().subscribe((msg: ChatWsMessage) => this.handleMessage(msg));
        this.subscribed = true;
      }
      this.isConnected = true;
    } catch (err) {
      console.error('Chat connection failed', err);
    } finally {
      this.isConnecting = false;
    }
  }

  typeEffect() {
    // ✅ STOP animation when chat window is open
    if (this.showWindow) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
      return;
    }

    const currentText = this.placeholders[this.placeholderIndex];

    if (!this.deleting) {
      this.displayPlaceholder = currentText.substring(0, this.charIndex + 1);
      this.charIndex++;

      if (this.charIndex === currentText.length) {
        this.deleting = true;
        this.timeout = setTimeout(() => this.typeEffect(), 2000);
        return;
      }
    } else {
      this.displayPlaceholder = currentText.substring(0, this.charIndex - 1);
      this.charIndex--;

      if (this.charIndex === 0) {
        this.deleting = false;
        this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholders.length;
      }
    }

    this.cd.detectChanges();

    this.timeout = setTimeout(() => this.typeEffect(), this.deleting ? 40 : 80);
  }

  ngOnDestroy() {
    clearTimeout(this.timeout);
    if (this.isBrowser) {
      document.body.classList.remove('chat-open');
    }
    clearTimeout(this.welcomeTimer);
    clearTimeout(this.welcomeAutoHideTimer);
  }

  async openInput() {
    this.dismissWelcome();
    this.isInputOpen = !this.isInputOpen;

    if (this.isInputOpen) {
      if (this.isBrowser) {
        document.body.classList.add('chat-open');
        this.isChatOpen = !this.isChatOpen;
      }

      // wait for DOM render
      if (this.isBrowser) {
        setTimeout(() => {
          this.chatInput?.nativeElement.focus();
        }, 100);
      }
    } else {
      if (this.isBrowser) {
        document.body.classList.remove('chat-open');
      }
      this.isChatOpen = !this.isChatOpen;
    }

    // ✅ Connect WebSocket immediately
    await this.initializeConnection();
  }

  closeBackdrop() {
    if (this.isBrowser) {
      this.isInputOpen = false;
      this.showWindow = false;
      document.body.classList.remove('chat-open');
      this.isChatOpen = !this.isChatOpen;
    }
  }

  //handle scroll to show/hide input
  handleScroll = () => {
    if (this.isBrowser) {
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

      if (currentScroll > this.lastScrollTop) {
        // scrolling down
        this.isScrollingDown = true;
      } else {
        // scrolling up
        this.isScrollingDown = false;
      }

      this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
    }
  };

  // --- TIME FORMAT: "12 Dec 25 03:40 PM" ---
  private nowTime(): string {
    return format(new Date(), 'dd MMM yy hh:mm:ss a');
  }

  // ------------------------------------------------------
  //  TOGGLE CHAT WINDOW
  // ------------------------------------------------------
  toggleWindow() {
    this.showWindow = !this.showWindow;

    if (this.showWindow) {
      clearTimeout(this.timeout);
      this.timeout = undefined; // ✅ REQUIRED
    } else {
      // ✅ restart safely
      if (!this.timeout) {
        setTimeout(() => {
          this.typeEffect();
        }, 100);
      }
    }

    if (!this.showWindow) {
      this.isInputOpen = !this.isInputOpen;
      this.isChatOpen = !this.isChatOpen;
    }

    if (this.showWindow) {
      setTimeout(() => {
        this.chatInput?.nativeElement.focus();
      }, 100);
    }

    if (this.showWindow && this.messages.length === 0) {
      this.messages.push({
        role: 'assistant',
        text: "👋 Hi there! I'm your Infraon Assistant. Ask me anything about our products, pricing, or features.",
        time: this.nowTime(),
      });
    }

    if (this.isBrowser) {
      document.body.classList.remove('chat-open');
    }
  }

  async send() {
    if (!this.input.trim()) return;

    if (!this.isConnected) {
      await this.initializeConnection();
    }

    if (this.chat.isClosed) {
      this.messages.push({
        role: 'system',
        text: '⚠️ Connection lost. Please reconnect first.',
        time: this.nowTime(),
      });
      return;
    }

    if (!this.showWindow) this.toggleWindow();

    // User message
    this.messages.push({
      role: 'user',
      text: this.input,
      time: this.nowTime(),
    });

    // Assistant typing bubble
    this.messages.push({
      role: 'assistant',
      text: '',
      typing: true,
      time: '',
    });

    this.streamingIndex = this.messages.length - 1;

    this.scrollToBottom();

    this.chat.sendPrompt(this.input);
    this.input = '';

    clearTimeout(this.timeout);
    this.timeout = undefined;
    this.displayPlaceholder = '';
  }

  // ------------------------------------------------------
  // HANDLE WS RESPONSES FROM SERVER
  // ------------------------------------------------------
  handleMessage(msg: ChatWsMessage) {
    if (this.streamingIndex === null) {
      console.warn('No active streaming message');
      return;
    }

    const current = this.messages[this.streamingIndex];

    if (!current) {
      this.streamingIndex = null;
      return;
    }

    if (msg.type === 'token') {
      if (current.typing) {
        current.typing = false;
        current.time = this.nowTime();
      }

      current.text += msg.data;
      this.applyLeadCaptureForm(current);
      this.cd.detectChanges();
      this.scrollToBottom();
    } else if (msg.type === 'done') {
      current.typing = false;
      this.streamingIndex = null;

      this.applyLeadCaptureForm(current);
      this.cd.detectChanges();
    } else if (msg.type === 'error') {
      current.typing = false;
      this.streamingIndex = null;

      this.messages.push({
        role: 'system',
        text: 'Error: ' + msg.message,
        time: this.nowTime(),
      });
      this.cd.detectChanges();
    }
  }

  // ------------------------------------------------------
  // LEAD-CAPTURE FORM DETECTION
  // Parses numbered/bulleted list items (e.g. "1) Your Name",
  // "- Email ID:") out of an assistant response and turns them
  // into real input fields instead of showing them as plain text.
  // ------------------------------------------------------
  private readonly formItemRegex = /^\s*(?:[-*]|\d+[).])\s*(.+?)\s*:?\s*$/;

  private extractFormFields(text: string): {
    label: string;
    key: string;
    type: string;
    value: string;
    required: boolean;
    countryCode?: string;
  }[] {
    const fields: {
      label: string;
      key: string;
      type: string;
      value: string;
      required: boolean;
      countryCode?: string;
    }[] = [];

    for (const line of text.split('\n')) {
      const match = line.match(this.formItemRegex);
      if (!match) continue;

      const label = match[1].trim();
      if (!label || label.length > 60) continue;

      const type = this.inputTypeForLabel(label);

      fields.push({
        label,
        key: label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, ''),
        type,
        value: '',
        required: /name|email|mail/i.test(label),
        ...(type === 'tel' ? { countryCode: '+91' } : {}),
      });
    }

    return fields;
  }

  // Drops the numbered/bulleted field-list lines from the assistant's
  // reply since they're now rendered as real inputs below the bubble.
  private stripFormFieldLines(text: string): string {
    return text
      .split('\n')
      .filter((line) => !this.formItemRegex.test(line))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private inputTypeForLabel(label: string): string {
    const l = label.toLowerCase();
    if (l.includes('email') || l.includes('mail')) return 'email';
    if (l.includes('phone') || l.includes('number')) return 'tel';
    return 'text';
  }

  // Re-run on every token (some flows never send a "done" event) so a
  // form still appears even if fields keep streaming in after the first
  // couple of lines arrive. Already-typed values are preserved by key,
  // and nothing recomputes once the form has been submitted.
  private applyLeadCaptureForm(message: {
    text: string;
    displayText?: string;
    formSubmitted?: boolean;
    contactForm?: boolean;
    formFields?: {
      label: string;
      key: string;
      type: string;
      value: string;
      required: boolean;
      error?: string;
      countryCode?: string;
    }[];
  }) {
    if (message.formSubmitted) return;

    const fields = this.extractFormFields(message.text);
    if (!this.isLeadCaptureForm(fields)) return;

    const existingByKey = new Map((message.formFields ?? []).map((f) => [f.key, f]));
    message.contactForm = true;
    message.formFields = fields.map((f) => existingByKey.get(f.key) ?? f);
    message.displayText = this.stripFormFieldLines(message.text);
  }

  private isLeadCaptureForm(fields: { label: string }[]): boolean {
    if (fields.length < 2) return false;
    const hasName = fields.some((f) => /name/i.test(f.label));
    const hasContact = fields.some((f) => /email|mail|phone|number/i.test(f.label));
    return hasName && hasContact;
  }

  // Same business-email rule as the email-cta widget: reject free/personal
  // and known-typo mail providers, prompting for a work email instead.
  private validateBusinessEmail(email: string): string {
    const trimmed = (email ?? '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmed) return 'Email is required';
    if (!emailRegex.test(trimmed)) return 'Please enter a valid email address';

    const domain = trimmed.split('@')[1]?.toLowerCase();
    if (this.BLOCKED_EMAIL_DOMAINS.includes(domain)) {
      return 'Please enter your business email address';
    }

    return '';
  }

  onFieldBlur(f: { type: string; value: string; error?: string }) {
    if (f.type === 'email') {
      f.error = this.validateBusinessEmail(f.value) || undefined;
    }
  }

  onCountryChange(f: { countryCode?: string }, e: { dialCode: string; iso2: string }) {
    f.countryCode = e.dialCode;
  }

  submitContactForm(m: {
    formFields?: {
      label: string;
      value: string;
      type: string;
      error?: string;
      countryCode?: string;
    }[];
    formSubmitted?: boolean;
  }) {
    if (!m.formFields?.length) return;

    let hasError = false;
    for (const f of m.formFields) {
      if (f.type === 'email') {
        f.error = this.validateBusinessEmail(f.value) || undefined;
        if (f.error) hasError = true;
      }
    }
    if (hasError) return;

    m.formSubmitted = true;

    const summary = m.formFields
      .map((f) => {
        const value = f.value?.trim();
        if (!value) return `${f.label}: -`;
        const prefix = f.type === 'tel' && f.countryCode ? `${f.countryCode} ` : '';
        return `${f.label}: ${prefix}${value}`;
      })
      .join('\n');

    this.messages.push({ role: 'user', text: summary, time: this.nowTime() });
    this.messages.push({ role: 'assistant', text: '', typing: true, time: '' });
    this.streamingIndex = this.messages.length - 1;

    this.scrollToBottom();
    this.chat.sendPrompt(summary);
  }

  // ------------------------------------------------------
  // RECONNECT WS
  // ------------------------------------------------------
  async reconnect() {
    this.messages.push({ role: 'system', text: '🔄 Reconnecting...', time: this.nowTime() });

    await this.chat.reconnect();
    await new Promise((res) => setTimeout(res, 1000));

    if (!this.chat.isClosed) {
      this.messages.push({
        role: 'system',
        text: '✅ Reconnected successfully!',
        time: this.nowTime(),
      });
    } else {
      this.messages.push({
        role: 'system',
        text: '❌ Reconnect failed. Try again.',
        time: this.nowTime(),
      });
    }
  }

  // ------------------------------------------------------
  // AUTO-SCROLL
  // ------------------------------------------------------
  scrollToBottom() {
    try {
      setTimeout(() => {
        const el = this.chatBody?.nativeElement;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    } catch {
      // chatBody not yet rendered - nothing to scroll
    }
  }

  private isResizing = false;
  private startY = 0;
  private startHeight = 0;

  startResize(event: MouseEvent) {
    if (!this.isBrowser) return;
    event.preventDefault();

    const el = this.chatWindow?.nativeElement;
    if (!el) return;

    this.isResizing = true;
    this.startY = event.clientY;
    this.startHeight = el.offsetHeight;

    document.addEventListener('mousemove', this.resizeMove);
    document.addEventListener('mouseup', this.stopResize);
  }

  resizeMove = (event: MouseEvent) => {
    if (!this.isBrowser) return;
    if (!this.isResizing) return;
    if (this.resizeFrame) return;

    this.resizeFrame = requestAnimationFrame(() => {
      const diff = this.startY - event.clientY;
      const newHeight = this.startHeight + diff;

      const min = 250;
      const max = window.innerHeight * 0.9;

      const el = this.chatWindow?.nativeElement;
      if (el) {
        el.style.height = Math.max(min, Math.min(max, newHeight)) + 'px';
      }

      this.resizeFrame = null;
    });
  };

  stopResize = () => {
    if (!this.isBrowser) return;
    this.isResizing = false;

    document.removeEventListener('mousemove', this.resizeMove);
    document.removeEventListener('mouseup', this.stopResize);
  };

  toggleTheme() {
    this.themeService.toggleTheme();
    this.isDarkMode = this.themeService.isDarkMode();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (!this.isBrowser) return;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    const quarter = (document.documentElement.scrollHeight - window.innerHeight) * 0.25;
    this.showButton = scrollTop >= quarter;
  }

  scrollToTop() {
    if (this.isBrowser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
